import { Directive, ElementRef, Input, AfterViewInit, Renderer2, HostListener } from '@angular/core';

/**
 * Directive to apply multiline ellipsis to an element.
 * It truncates the text content of the element to fit within a specified number of lines,
 * adding an ellipsis ("...") if the text overflows.
 * Usage:
 * ```html
 *  <div multiline-ellipsis="3">This is a long text that will be truncated after three lines.</div>
 * ```
 * The number of lines can be specified as an input to the directive.
 * If no number is provided, it defaults to 2 lines.
 *
 */

@Directive({
  selector: '[multiline-ellipsis]'
})
export class MultilineEllipsisDirective implements AfterViewInit {
  @Input('multiline-ellipsis') lines: string = '2';
  private originalText: string = '';

  constructor(private el: ElementRef, private renderer: Renderer2) {}

  ngAfterViewInit(): void {
    this.originalText = this.el.nativeElement.textContent.trim();
    this.applyEllipsis();
  }

  @HostListener('window:resize')
  onResize() {
    this.resetText();
    this.applyEllipsis();
  }

  private resetText() {
    this.el.nativeElement.textContent = this.originalText;
  }

  private applyEllipsis() {
    const element = this.el.nativeElement;
    const style = getComputedStyle(element);
    const lineHeight = parseFloat(style.lineHeight);

    if (!lineHeight || isNaN(lineHeight)) return;

    const maxHeight = parseInt(this.lines) * lineHeight;
    this.renderer.setStyle(element, 'maxHeight', `${maxHeight}px`);
    this.renderer.setStyle(element, 'overflow', 'hidden');

    while (element.scrollHeight > element.clientHeight) {
      element.textContent = element.textContent.replace(/\W*\s(\S)*$/, '...');
    }
  }
}
